import { Fragment, useMemo, useState, type ReactNode } from 'react';

import Widget from '@/components/Widget/Widget';
import { cn } from '@/lib/cn';

import { BUCKET_COLOR_VARS, EXPENSIVE_CENTS } from './config';
import { aggregateDayToHourly, bucketFor, cheapestHourWindowInDay, type CheapestHourWindow, type HourPrice } from './electricityDerive';
import ElectricityDetailModal from './ElectricityDetailModal';
import useElectricity from './useElectricity';

const WINDOW_HOURS = 3;
// Before day-ahead prices are published the feed can contain a stray hour or two of tomorrow.
const TOMORROW_READY_MIN_HOURS = 20;

function pad2(n: number): string {
  return String(((n % 24) + 24) % 24).padStart(2, '0');
}

function fmtWindow(w: CheapestHourWindow): string {
  return `${pad2(w.startHour)}–${pad2(w.endHour + 1)}`;
}

const GRID_STEPS = [5, 10, 20, 25, 50, 100];

/** Gridline values below the scale max: at most 3 intervals so labels stay legible on the 48 px phone chart. */
function gridLines(maxCents: number): number[] {
  const step = GRID_STEPS.find((s) => maxCents / s <= 3) ?? Math.ceil(maxCents / 3);
  const lines: number[] = [];
  // Skip lines too close to the top, where the max label sits.
  for (let v = step; v < maxCents * 0.85; v += step) lines.push(v);
  return lines;
}

export default function ElectricityWidget() {
  const [open, setOpen] = useState(false);
  const { data, isLoading, isError, error, refetch, dataUpdatedAt } = useElectricity();

  const derived = useMemo(() => {
    if (!data) return null;
    const nowMs = Date.now();
    const today = aggregateDayToHourly(data.today, nowMs);
    const tomorrow = aggregateDayToHourly(data.tomorrow, nowMs);
    // Floor the scale so cheap days read as short bars instead of always filling the chart.
    const maxCents = Math.max(EXPENSIVE_CENTS, ...today.map((h) => h.cents), ...tomorrow.map((h) => h.cents));
    const currentHour = today.find((h) => h.isCurrent)?.hour ?? today.find((h) => !h.isPast)?.hour ?? 0;
    return {
      today,
      tomorrow,
      maxCents,
      cheapestToday: cheapestHourWindowInDay(today, WINDOW_HOURS, currentHour),
      cheapestTomorrow: cheapestHourWindowInDay(tomorrow, WINDOW_HOURS, 0),
      peakToday: today.length ? today.reduce((a, b) => (b.cents > a.cents ? b : a)) : null,
    };
  }, [data]);

  const status = isLoading ? 'loading' : isError ? 'error' : !data?.current ? 'empty' : 'loaded';

  return (
    <>
      <Widget
        status={status}
        errorMessage={error instanceof Error ? error.message : 'Failed to load prices'}
        emptyMessage="No price data available"
        loadingMessage="Loading prices…"
        onOpenDetail={() => setOpen(true)}
        className="border-t-[3px] border-t-(--accent-success)"
      >
        {data && data.current && derived && (
          <div className="@container flex h-full flex-col gap-4">
            <div className="flex items-baseline gap-2.5 pr-6 whitespace-nowrap">
              <span className="text-[11px] font-semibold tracking-[0.14em] text-(--text-tertiary) uppercase">Electricity</span>
              <span className="font-display text-3xl leading-none font-semibold text-(--text-primary) tabular-nums @xl:text-4xl">
                {data.current.cents.toFixed(1)}
              </span>
              <span className="text-sm text-(--text-tertiary)">¢/kWh now</span>
            </div>

            <HourRow
              label="Today"
              stat={
                derived.peakToday && (
                  <>
                    peak <span className="font-medium text-(--accent-danger)">{derived.peakToday.cents.toFixed(1)} ¢</span> at{' '}
                    {pad2(derived.peakToday.hour)}
                  </>
                )
              }
              hours={derived.today}
              maxCents={derived.maxCents}
              cheapest={derived.cheapestToday}
              dimPast
            />
            {derived.tomorrow.length >= TOMORROW_READY_MIN_HOURS ? (
              <HourRow
                label="Tomorrow"
                stat={data.tomorrowAverageCents != null && <>avg {data.tomorrowAverageCents.toFixed(1)} ¢</>}
                hours={derived.tomorrow}
                maxCents={derived.maxCents}
                cheapest={derived.cheapestTomorrow}
              />
            ) : (
              <p className="text-xs text-(--text-muted)">Tomorrow's prices are published around 14:00.</p>
            )}
          </div>
        )}
      </Widget>

      <ElectricityDetailModal
        open={open}
        onClose={() => setOpen(false)}
        data={data}
        lastUpdated={dataUpdatedAt ? new Date(dataUpdatedAt) : null}
        onRefresh={() => refetch()}
      />
    </>
  );
}

const SCALE_LABEL = 'absolute left-full translate-y-1/2 pl-1.5 text-[10px] leading-none whitespace-nowrap text-(--text-muted) tabular-nums';

interface HourRowProps {
  label: string;
  /** Muted secondary figure shown next to the label. */
  stat?: ReactNode;
  hours: HourPrice[];
  maxCents: number;
  cheapest: CheapestHourWindow | null;
  dimPast?: boolean;
}

function HourRow({ label, stat, hours, maxCents, cheapest, dimPast = false }: HourRowProps) {
  const cheapestStartIdx = cheapest ? hours.findIndex((h) => h.hour === cheapest.startHour) : -1;

  return (
    // Wide tiles have a definite height, so the chart flexes to fill it; elsewhere it's a fixed 48 px.
    <div className="tablet-hz:min-h-0 tablet-hz:flex-1 flex flex-col">
      <div className="mb-2.5 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 text-xs">
        <span className="flex items-baseline gap-2">
          <span className="font-semibold text-(--text-secondary)">{label}</span>
          {stat && <span className="text-(--text-muted) tabular-nums">{stat}</span>}
        </span>
        {cheapest && (
          <span className="text-(--text-muted) tabular-nums">
            cheapest{' '}
            <span className="font-medium text-(--accent-success)">
              {fmtWindow(cheapest)} · {cheapest.avgCents.toFixed(1)} ¢
            </span>
          </span>
        )}
      </div>
      <div className="tablet-hz:flex tablet-hz:min-h-0 tablet-hz:flex-1 tablet-hz:flex-col pr-8">
        <div className="tablet-hz:h-auto tablet-hz:min-h-20 tablet-hz:flex-1 relative flex h-12 items-end gap-px border-t border-dashed border-(--border-default)">
          <span className={cn(SCALE_LABEL, 'bottom-full')}>{Math.round(maxCents)} ¢</span>
          {gridLines(maxCents).map((v) => {
            const bottom = `${(v / maxCents) * 100}%`;
            return (
              <Fragment key={v}>
                <span aria-hidden="true" className="absolute inset-x-0 border-t border-(--border-default)" style={{ bottom }} />
                <span className={SCALE_LABEL} style={{ bottom }}>
                  {v}
                </span>
              </Fragment>
            );
          })}
          {hours.map((h) => (
            <span
              key={h.hour}
              className={cn('relative flex-1 rounded-t-xs', dimPast && h.isPast && 'opacity-30')}
              style={{
                height: `${Math.max(4, (h.cents / maxCents) * 100)}%`,
                background: `var(${BUCKET_COLOR_VARS[bucketFor(h.cents)]})`,
                outline: h.isCurrent ? '1.5px solid var(--text-primary)' : undefined,
                outlineOffset: h.isCurrent ? 1 : undefined,
              }}
              title={`${pad2(h.hour)}:00 · ${h.cents.toFixed(1)} ¢`}
            />
          ))}
        </div>
        <div className="relative mt-1 h-1">
          {cheapest && cheapestStartIdx >= 0 && (
            <span
              className="absolute inset-y-0 rounded-full bg-(--accent-success)"
              style={{
                left: `${(cheapestStartIdx / hours.length) * 100}%`,
                width: `${(WINDOW_HOURS / hours.length) * 100}%`,
              }}
            />
          )}
        </div>
        <div className="mt-0.5 flex text-[11px] text-(--text-muted) tabular-nums">
          <span className="flex-1">00</span>
          <span className="flex-1">06</span>
          <span className="flex-1">12</span>
          <span className="flex-1">18</span>
        </div>
      </div>
    </div>
  );
}
